// PlayerProfile Component
// Main component for displaying player profiles

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { FaEdit, FaShare, FaGlobe, FaUsers, FaLock, FaChartLine, FaArrowLeft, FaUser, FaTrophy, FaStar, FaCalendarCheck, FaEye, FaEnvelope } from 'react-icons/fa';
import styles from './PlayerProfile.module.css';
import { usePlayerProfile } from '../../hooks/playerProfile/usePlayerProfile';
import { useProfilePrivacy } from '../../hooks/playerProfile/useProfilePrivacy';
import { useNotification } from '../../context/NotificationContext';
import { useUser } from '../../context/UserContext';
import ProfileCompleteness from '../../components/ProfileComponents/ProfileCompleteness';
import PrivacyBadge from '../../components/ProfileComponents/PrivacyBadge';
import ShareProfile from '../../components/ProfileComponents/ShareProfile';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ProfileSkeleton, { TabSkeleton } from '../../components/ProfileComponents/ProfileSkeleton';
import ProfileErrorBoundary from '../../components/ProfileComponents/ProfileErrorBoundary';
import ProfileMeta from '../../components/ProfileComponents/ProfileMeta';
import { performanceMonitor, usePerformanceTracking } from '../../utils/playerProfile/performanceMonitor';
import { warmCache } from '../../utils/playerProfile/profileCache';
import { getAriaLabels } from '../../utils/playerProfile/accessibility';
import { calculateProfileCompleteness, calculateAge } from '../../utils/playerProfile/profileHelpers';
import playerProfileService from '../../services/playerProfile/playerProfileService';
import ProfileCard from '../../components/ProfileComponents/ProfileCard';
import ProfileSidebar from '../../components/ProfileComponents/ProfileSidebar';
import ProfileAnalytics from '../../components/ProfileComponents/ProfileAnalytics';
import ViewAsSelector from '../../components/ProfileComponents/ViewAsSelector';

// Lazy load profile sections for performance
const AthleticProfile = lazy(() => import('./sections/AthleticProfile'));
const Experience = lazy(() => import('./sections/Experience'));
const Skills = lazy(() => import('./sections/Skills'));
const Awards = lazy(() => import('./sections/Awards'));
const ClubHistory = lazy(() => import('./sections/ClubHistory'));
const EventStats = lazy(() => import('./sections/EventStats'));
const Media = lazy(() => import('./sections/Media'));
const Availability = lazy(() => import('./sections/Availability'));

const PlayerProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showInfo, showSuccess, showError } = useNotification();
  const { user: currentUser, userProfile } = useUser();
  const hasNavigationHistory = location.key !== 'default';

  // Performance tracking
  usePerformanceTracking('PlayerProfile');

  // State
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [previewMode, setPreviewMode] = useState('owner'); // 'owner', 'public', 'club'
  const [sidebarStats, setSidebarStats] = useState({
    awards: 0,
    clubs: 0,
    events: 0
  });

  // Hooks
  const {
    profile,
    loading,
    error,
    isOwner,
    allowedSections,
    isVerified,
    isFeatured,
    isSectionAllowed,
    getPrivacyLevel,
    profileNotFound
  } = usePlayerProfile(userId, { previewMode });
  
  // Calculate completeness on frontend (backend value is often stale)
  const profileCompleteness = profile ? calculateProfileCompleteness(profile) : 0;

  const {
    isAuthenticated
  } = useProfilePrivacy(profile, userProfile?._id || currentUser?.uid, { previewMode });
  
  // Fetch stats for sidebar
  useEffect(() => {
    const fetchStats = async () => {
      if (!profile || !profile.userId) return;

      try {
        const userId = profile.userId._id || profile.userId;
        
        // Fetch aggregated stats
        const [awardsData, eventsData] = await Promise.all([
          isSectionAllowed('awards') 
            ? playerProfileService.getAggregatedAwards(userId).catch(() => null)
            : Promise.resolve(null),
          isSectionAllowed('events')
            ? playerProfileService.getEventStats(userId).catch(() => null)
            : Promise.resolve(null)
        ]);

        // Calculate clubs from experience data
        const clubsCount = profile?.experience?.filter(exp => exp.type === 'club').length || 0;

        setSidebarStats({
          awards: awardsData?.total || 0,
          clubs: clubsCount,
          events: eventsData?.eventsAttended || 0
        });
      } catch (err) {
        console.error('Error fetching sidebar stats:', err);
      }
    };

    fetchStats();
  }, [profile, isSectionAllowed]);

  // Handle edit profile
  const handleEditProfile = () => {
    // Must use MongoDB user ID for edit page
    const editUserId = userProfile?._id;
    if (editUserId) {
      navigate(`/player-profile/${editUserId}/edit`);
    } else {
      showError('Please wait for your profile to be initialized');
    }
  };

  // Handle view analytics
  const handleViewAnalytics = () => {
    setShowAnalytics(true);
  };

  // Handle share profile
  const handleShareProfile = () => {
    setShowShareModal(true);
  };

  // Render loading state
  if (loading) {
    return <ProfileSkeleton />;
  }

  // Render profile not found state
  if (profileNotFound) {
    // Check if this is the current user's profile
    // IMPORTANT: Must check AFTER userProfile has loaded
    const isCurrentUserProfile = 
      (userProfile && userProfile._id === userId) || 
      (currentUser && currentUser.uid === userId);
    
    // ALWAYS show create page for own profile, even if isOwner hasn't updated yet
    if (isOwner || isCurrentUserProfile) {
      // For profile creation, we must use the MongoDB user ID
      const profileUserId = userProfile?._id;
      
      if (!profileUserId) {
        return (
          <div className={styles.container}>
            <div className={styles.loadingContainer}>
              <LoadingSpinner size="large" />
              <p>Setting up your profile...</p>
            </div>
          </div>
        );
      }
      
      return (
        <div className={styles.container}>
          <div className={styles.emptyStateContainer}>
            <div className={styles.emptyStateContent}>
              <FaUser className={styles.emptyStateIcon} />
              <h2>Create Your Player Profile</h2>
              <p>You haven't created your player profile yet. Build your professional sports profile to showcase your skills, experience, and achievements.</p>
              
              <div className={styles.emptyStateFeatures}>
                <div className={styles.feature}>
                  <FaChartLine className={styles.featureIcon} />
                  <span>Track your athletic journey</span>
                </div>
                <div className={styles.feature}>
                  <FaUsers className={styles.featureIcon} />
                  <span>Connect with clubs and scouts</span>
                </div>
                <div className={styles.feature}>
                  <FaGlobe className={styles.featureIcon} />
                  <span>Showcase your achievements</span>
                </div>
              </div>
              
              <Link 
                to={`/player-profile/${profileUserId}/edit`}
                className={styles.createProfileButton}
              >
                Create My Profile
              </Link>
            </div>
          </div>
        </div>
      );
    }

    // For non-owners viewing a non-existent profile
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Profile Not Found</h2>
          <p>This player hasn't created their profile yet.</p>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  // Render other error state
  if (error && !profileNotFound) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Error Loading Profile</h2>
          <p>{error}</p>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  // Render no profile state (shouldn't happen with profileNotFound logic)
  if (!profile) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Profile Not Found</h2>
          <p>The profile you're looking for doesn't exist or has been removed.</p>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  // Name priority: profile.name > profile.firstName/lastName > User.firstName/lastName > fallback
  const userName = profile.name
    || ((profile.firstName || profile.lastName)
        ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
        : null)
    || ((profile.userId?.firstName || profile.userId?.lastName)
        ? `${profile.userId.firstName || ''} ${profile.userId.lastName || ''}`.trim()
        : null)
    || profile.userId?.name
    || 'Player';
  // Handle new Cloudinary structure: media.profileImage.cloudinary.url
  const userPhoto = profile.userId?.profilePicture || 
                   profile.media?.profileImage?.cloudinary?.url || 
                   profile.media?.profileImage?.url ||
                   profile.profilePicture;
  const ariaLabels = profile ? getAriaLabels(profile) : {};

  return (
    <>
      <ProfileMeta profile={profile} isOwner={isOwner} />
      <div className={styles.container} role="main" id="main-content">
      {/* Cover Photo Section */}
      <div 
        className={styles.coverPhoto}
        style={{
          backgroundImage: (() => {
            // Extract cover photo URL - handle multiple formats
            let coverUrl = '';
            if (typeof profile.coverPhoto === 'string') {
              coverUrl = profile.coverPhoto;
            } else if (profile.coverPhoto?.cloudinary?.url) {
              coverUrl = profile.coverPhoto.cloudinary.url;
            } else if (profile.coverPhoto?.url) {
              coverUrl = profile.coverPhoto.url;
            } else if (profile.coverPhoto?.cloudinary?.transformedUrls?.desktop) {
              coverUrl = profile.coverPhoto.cloudinary.transformedUrls.desktop;
            }
            
            return coverUrl 
              ? `url(${coverUrl})` 
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
          })()
        }}
        role="img"
        aria-label={ariaLabels.coverPhoto}
      >
        <div className={styles.coverOverlay} />
        {hasNavigationHistory && (
          <button
            className={styles.coverBackButton}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <FaArrowLeft /> Back
          </button>
        )}
      </div>

      {/* View As Selector - Top Right Corner - Only for profile owner */}
      {isOwner && (
        <div className={styles.viewAsFloating}>
          <ViewAsSelector 
            currentMode={previewMode}
            onChange={setPreviewMode}
          />
        </div>
      )}

      {/* Profile Header */}
      <div className={styles.profileHeader}>
        <div className={styles.profilePicture}>
          {userPhoto ? (
            <img src={userPhoto} alt={ariaLabels.profilePicture} />
          ) : (
            <div className={styles.profilePicturePlaceholder} aria-label={ariaLabels.profilePicture}>
              {userName.charAt(0).toUpperCase()}
            </div>
          )}
          {isVerified && (
            <div className={styles.verifiedBadge} title="Verified Profile" aria-label="Verified badge">✓</div>
          )}
        </div>

        <div className={styles.profileInfo}>
          <h1 className={styles.profileName}>
            {userName}
            {isFeatured && <span className={styles.featuredBadge}>⭐ Featured</span>}
          </h1>
          {profile.headline && (
            <p className={styles.profileHeadline}>{profile.headline}</p>
          )}
          {/* Resume-Style Contact Info */}
          <div className={styles.resumeContact}>
            {profile.location && (
              <span className={styles.contactDetail}>
                📍 {[profile.location.city, profile.location.state, profile.location.country]
                  .filter(val => typeof val === 'string' && val)
                  .join(', ') || 'Location not specified'}
              </span>
            )}
            {profile.dateOfBirth && (
              <span className={styles.contactDetail}>
                🎂 {calculateAge(profile.dateOfBirth)} years
              </span>
            )}
            {profile.contactPreferences?.email?.visible && profile.contactPreferences?.email?.value && (
              <a 
                href={`mailto:${profile.contactPreferences.email.value}`} 
                className={styles.contactDetail}
              >
                📧 {profile.contactPreferences.email.value}
              </a>
            )}
            {profile.contactPreferences?.phone?.visible && profile.contactPreferences?.phone?.value && (
              <a 
                href={`tel:${profile.contactPreferences.phone.value}`} 
                className={styles.contactDetail}
              >
                📱 {profile.contactPreferences.phone.value}
              </a>
            )}
          </div>
          {/* Quick Info Inline */}
          <div className={styles.quickInfo}>
            {profile.athleticProfile?.primarySport && (
              <span className={styles.infoItem}>
                {profile.athleticProfile.primarySport}
              </span>
            )}
            {profile.athleticProfile?.positions?.[0] && (
              <span className={styles.infoItem}>
                {profile.athleticProfile.positions[0].position}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Preview Mode Banner */}
      {isOwner && previewMode !== 'owner' && (
        <div className={styles.previewBanner}>
          <div className={styles.previewBannerContent}>
            <div className={styles.previewIconWrapper}>
              <FaEye className={styles.previewIcon} />
            </div>
            <div className={styles.previewText}>
              <div className={styles.previewLabel}>Preview Mode</div>
              <div className={styles.previewValue}>
                Viewing as {previewMode === 'public' ? '🌐 Public Visitor' : '👥 Club Member'}
              </div>
            </div>
            <button 
              className={styles.exitPreview}
              onClick={() => setPreviewMode('owner')}
            >
              Exit Preview
            </button>
          </div>
        </div>
      )}

      {/* LinkedIn-Style 2-Column Layout */}
      <div className={styles.contentLayout}>
        {/* Main Content Column */}
        <div className={styles.mainContent}>
          <ProfileErrorBoundary section="profile-content">
            <Suspense fallback={<TabSkeleton />}>
              
              {/* About Section */}
              <ProfileCard 
                title="About" 
                icon={FaUser}
                id="about"
                isEmpty={!profile.bio}
                emptyMessage="No bio added yet."
              >
                {profile.bio && <p className={styles.bioText}>{profile.bio}</p>}
              </ProfileCard>

              {/* Experience Section */}
              {isSectionAllowed('experience') && (
                <ProfileCard 
                  title="Experience" 
                  icon={FaChartLine}
                  id="experience"
                  isEmpty={!profile.experience || profile.experience.length === 0}
                  emptyMessage="No experience added yet."
                >
                  <Experience 
                    experience={profile.experience}
                    isOwner={isOwner}
                  />
                </ProfileCard>
              )}

              {/* Athletic Profile Section */}
              {isSectionAllowed('athleticProfile') && (
                <ProfileCard 
                  title="Athletic Profile" 
                  icon={FaChartLine}
                  id="athletic"
                >
                  <AthleticProfile 
                    athleticProfile={profile.athleticProfile}
                    isOwner={isOwner}
                  />
                </ProfileCard>
              )}

              {/* Skills Section */}
              {isSectionAllowed('skills') && (
                <ProfileCard 
                  title="Skills" 
                  icon={FaStar}
                  id="skills"
                  isEmpty={!profile.skills || Object.keys(profile.skills).length === 0}
                  emptyMessage="No skills added yet."
                >
                  <Skills 
                    skills={profile.skills}
                    userId={userId}
                    isOwner={isOwner}
                    currentUserId={userProfile?._id || currentUser?.uid}
                  />
                </ProfileCard>
              )}

              {/* Awards Section */}
              {isSectionAllowed('awards') && (
                <ProfileCard 
                  title="Awards & Achievements" 
                  icon={FaTrophy}
                  id="awards"
                >
                  <Awards 
                    userId={userId}
                    isOwner={isOwner}
                  />
                </ProfileCard>
              )}

              {/* Club History Section */}
              {isSectionAllowed('clubHistory') && (
                <ProfileCard 
                  title="Club History" 
                  icon={FaUsers}
                  id="clubs"
                >
                  <ClubHistory 
                    userId={userId}
                    isOwner={isOwner}
                  />
                </ProfileCard>
              )}

              {/* Availability Section */}
              {isSectionAllowed('availability') && (
                <ProfileCard 
                  title="Availability" 
                  icon={FaCalendarCheck}
                  id="availability"
                >
                  <Availability 
                    availability={profile.availability}
                    isOwner={isOwner}
                  />
                </ProfileCard>
              )}

              {/* Media Section */}
              {isSectionAllowed('media') && (
                <ProfileCard 
                  title="Media Gallery" 
                  icon={FaGlobe}
                  id="media"
                >
                  <Media 
                    media={profile.media}
                    userId={userId}
                    isOwner={isOwner}
                  />
                </ProfileCard>
              )}

            </Suspense>
          </ProfileErrorBoundary>
        </div>

        {/* Sidebar Column */}
        <aside className={styles.sidebarContent}>
          <ProfileSidebar 
            isOwner={isOwner}
            profileCompleteness={profileCompleteness}
            profileId={profile._id}
            stats={sidebarStats}
            onEdit={handleEditProfile}
            onViewAnalytics={handleViewAnalytics}
            onShare={handleShareProfile}
          />
        </aside>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareProfile
          profileUrl={`${window.location.origin}/player-profile/${userId}`}
          profileName={userName}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Privacy Notice for Non-Authenticated Users */}
      {!isAuthenticated && (
        <div className={styles.privacyNotice}>
          <FaLock />
          <p>
            <Link to="/login">Sign in</Link> to see more profile sections and connect with this player.
          </p>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalytics && isOwner && (
        <ProfileAnalytics
          userId={userId}
          onClose={() => setShowAnalytics(false)}
        />
      )}
      </div>
    </>
  );
};

export default PlayerProfile;
